import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import type {
  AddProjectToUserMutation,
  AddProjectToUserMutationVariables,
  ChangePasswordMutation,
  ChangePasswordMutationVariables,
  GetUserProjectsQuery,
  GetUserProjectsQueryVariables,
  GetUserQuery,
  GetUserQueryVariables,
  LoginMutation,
  LoginMutationVariables,
  SignupMutation,
  SignupMutationVariables,
  UpdateUserMutation,
  UpdateUserMutationVariables,
} from '../generated/graphql'

// GraphQL Operations - Define outside hooks for reusability with TypedDocumentNode
const SIGNUP_MUTATION: TypedDocumentNode<
  SignupMutation,
  SignupMutationVariables
> = gql`
  mutation Signup($input: SignupInput!) {
    signup(input: $input) {
      user {
        id
        name
        email
        skills
      }
      errors
    }
  }
`

const LOGIN_MUTATION: TypedDocumentNode<LoginMutation, LoginMutationVariables> =
  gql`
    mutation Login($input: LoginInput!) {
      login(input: $input) {
        token {
          token
        }
        user {
          id
          email
          name
          projects {
            id
            name
            description
            contactEmail
            status
            createdAt
          }
          skills
        }
        errors
      }
    }
  `

const UPDATE_USER_MUTATION: TypedDocumentNode<
  UpdateUserMutation,
  UpdateUserMutationVariables
> = gql`
  mutation UpdateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        id
        name
        email
        skills
      }
      errors
    }
  }
`

const CHANGE_PASSWORD_MUTATION: TypedDocumentNode<
  ChangePasswordMutation,
  ChangePasswordMutationVariables
> = gql`
  mutation ChangePassword($input: ChangePasswordInput!) {
    changePassword(input: $input) {
      user {
        id
        name
        email
        skills
      }
      errors
    }
  }
`

const ADD_PROJECT_TO_USER_MUTATION: TypedDocumentNode<
  AddProjectToUserMutation,
  AddProjectToUserMutationVariables
> = gql`
  mutation AddProjectToUser($input: AddProjectToUserInput!) {
    addProjectToUser(input: $input) {
      user {
        id
        name
        email
        projects {
          id
          name
          description
          contactEmail
        }
      }
      errors
    }
  }
`

const GET_USER_QUERY: TypedDocumentNode<GetUserQuery, GetUserQueryVariables> =
  gql`
    query GetUser($id: ID!) {
      user(id: $id) {
        id
        name
        email
        skills
        createdAt
        updatedAt
        projects {
          id
          name
          description
          contactEmail
          createdAt
        }
      }
    }
  `

const GET_USER_PROJECTS_QUERY: TypedDocumentNode<
  GetUserProjectsQuery,
  GetUserProjectsQueryVariables
> = gql`
  query GetUserProjects($id: ID!) {
    user(id: $id) {
      projects {
        id
        name
        description
        contactEmail
      }
    }
  }
`

// Custom hooks - Use the operations defined above
export function useSignup(
  options?: useMutation.Options<SignupMutation, SignupMutationVariables>
) {
  return useMutation(SIGNUP_MUTATION, options)
}

export function useLogin(
  options?: useMutation.Options<LoginMutation, LoginMutationVariables>
) {
  return useMutation(LOGIN_MUTATION, options)
}

export function useUpdateUser() {
  return useMutation(UPDATE_USER_MUTATION)
}

export function useChangePassword() {
  return useMutation(CHANGE_PASSWORD_MUTATION)
}

export function useAddProjectToUser() {
  return useMutation(ADD_PROJECT_TO_USER_MUTATION)
}

export function useGetUser(id: string) {
  return useQuery(GET_USER_QUERY, {
    variables: { id },
    skip: !id, // Skip query if no ID provided
  })
}

export function useGetUserProjects(id: string) {
  return useQuery(GET_USER_PROJECTS_QUERY, {
    variables: { id },
    skip: !id, // Skip query if no ID provided
  })
}
